import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Video, VideoOff, Mic, MicOff, PhoneOff, Users, 
  Calendar, Clock, RefreshCw, Play, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminLiveStudioHost = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [inSession, setInSession] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  
  // WebRTC refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);

  useEffect(() => {
    fetchTodayBookings();
    const interval = setInterval(fetchTodayBookings, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchTodayBookings = async () => {
    try {
      const res = await fetch(`${API}/api/live-studio/admin/bookings`);
      if (res.ok) {
        const data = await res.json();
        // Filter to confirmed bookings
        const confirmed = data.bookings?.filter(b => b.status === 'confirmed') || [];
        setBookings(confirmed);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const joinSession = async (booking) => {
    setActiveSession(booking);
    
    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      localStreamRef.current = stream;
      
      // Initialize WebRTC
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      });
      
      peerConnectionRef.current = pc;
      
      // Add local tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      
      // Handle remote tracks
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
        toast.success('Client connected!');
      };
      
      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'ice-candidate',
            candidate: event.candidate
          }));
        }
      };
      
      // Connect to signaling server as HOST
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = API.replace('https://', '').replace('http://', '');
      console.log('Host connecting to room:', booking.id);
      const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/live-studio/ws/${booking.id}/host`);
      
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('Host WebSocket connected');
        setInSession(true);
        toast.success('Ready for client!');
      };
      
      ws.onmessage = async (event) => {
        const message = JSON.parse(event.data);
        
        if (message.type === 'peer_joined') {
          toast.info('Client joined the session');
        }
        
        if (message.type === 'offer') {
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'offer',
            sdp: message.sdp
          }));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          ws.send(JSON.stringify({
            type: 'answer',
            sdp: answer.sdp
          }));
        }
        
        if (message.type === 'ice-candidate' && message.candidate) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
          } catch (e) {
            console.error('Error adding ICE candidate:', e);
          }
        }
        
        if (message.type === 'peer_left') {
          toast.info('Client disconnected');
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error');
      };
      
    } catch (err) {
      console.error('Media error:', err);
      toast.error('Could not access camera/microphone');
    }
  };

  const endSession = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    localStreamRef.current = null;
    peerConnectionRef.current = null;
    wsRef.current = null;
    
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    
    setInSession(false);
    setActiveSession(null);
    toast.info('Session ended');
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setMicEnabled(track.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setVideoEnabled(track.enabled);
      }
    }
  };

  if (inSession && activeSession) {
    return (
      <div className="min-h-screen bg-black text-white">
        {/* Video Area */}
        <div className="relative h-[calc(100vh-100px)]">
          {/* Remote Video (Client) - Main View */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
          
          {/* Placeholder when no remote video */}
          {!remoteVideoRef.current?.srcObject && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-black">
              <Users className="w-20 h-20 text-gray-600 mb-4" />
              <h3 className="text-xl font-bold">Waiting for {activeSession.customer_name}...</h3>
              <p className="text-gray-400 mt-2">They'll appear here when they join</p>
            </div>
          )}
          
          {/* Local Video (You) - Picture in Picture */}
          <div className="absolute bottom-4 right-4 w-64 aspect-video rounded-xl overflow-hidden border-2 border-green-500 shadow-2xl">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Session Info */}
          <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 text-green-500 mb-1">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-bold">LIVE - HOST</span>
            </div>
            <p className="text-sm text-gray-300">{activeSession.customer_name}</p>
            <p className="text-xs text-gray-500">{activeSession.date} at {activeSession.time}</p>
          </div>
        </div>
        
        {/* Controls */}
        <div className="h-[100px] bg-[#141414] flex items-center justify-center gap-4">
          <button
            onClick={toggleMic}
            className={`w-14 h-14 rounded-full flex items-center justify-center ${
              micEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500'
            }`}
          >
            {micEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </button>
          
          <button
            onClick={toggleVideo}
            className={`w-14 h-14 rounded-full flex items-center justify-center ${
              videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500'
            }`}
          >
            {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </button>
          
          <button
            onClick={endSession}
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Video className="w-8 h-8 text-green-500" />
              Live Studio Host
            </h1>
            <p className="text-gray-400 mt-1">Join sessions with your clients</p>
          </div>
          <button
            onClick={fetchTodayBookings}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Upcoming Sessions */}
        <div className="bg-[#141414] rounded-2xl p-6">
          <h2 className="text-xl font-bold mb-6">Confirmed Sessions</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No confirmed sessions</p>
              <p className="text-sm mt-2">Sessions will appear here once clients pay</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0a0a0a] rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center text-black font-bold text-lg">
                      {booking.customer_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <h3 className="font-semibold">{booking.customer_name}</h3>
                      <p className="text-sm text-gray-400">{booking.customer_email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-gray-400">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{booking.date}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-400 mt-1">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{booking.time}</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => joinSession(booking)}
                      className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-xl flex items-center gap-2"
                    >
                      <Play className="w-5 h-5" />
                      Join Session
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-400">How it works</h3>
              <ul className="text-sm text-gray-300 mt-2 space-y-1">
                <li>• When a client books and pays, their session appears here</li>
                <li>• Click "Join Session" to start your camera and wait for the client</li>
                <li>• The client joins from their confirmation email or the /live-studio page</li>
                <li>• You'll see each other in real-time video for the recording session</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLiveStudioHost;
